import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  calcSignature,
  chapterAt,
  chaptersOf,
  filledTextCount,
  isUsableTarget,
  pickOpenerIndex,
  progressPercent,
  resumeIndex,
  TOUR_SHAPE_TYPES,
} from "./tourProgress";

/**
 * Интерактивное знакомство с доской.
 *
 * Не список горячих клавиш (он есть в «Справке»), а проводка по самой доске.
 * Половина шагов — не «нажмите кнопку», а «сделайте»: нарисуйте, выделите,
 * передвиньте, сотрите, поставьте виджет. Шаг засчитывается по РЕЗУЛЬТАТУ на
 * доске, а не по нажатию: сделанное руками остаётся, прочитанное забывается
 * к следующему занятию.
 *
 * Шаг привязан к живому элементу, а не к координатам: панель переезжает на
 * узком экране, часть кнопок прячется в «Меню». Если цель спрятана внутри
 * окошка инструмента, шаг открывает его сам (`prepare`), а если кнопки нет
 * совсем — шаг так и говорит и листается дальше.
 */

type TourCtx = {
  objects: () => Record<string, unknown>[];
  selection: () => string[];
  count: () => number;
};

export type TourStep = {
  id: string;
  /** Цели по порядку: берём первую, что нашлась на экране. Пусто — доска вся. */
  targets?: string[];
  /**
   * Что нажать, если цели не видно. Список — ЦЕПОЧКА: сначала открывается
   * окошко инструмента, потом кнопка внутри него. За один заход нажимаем
   * самое глубокое из доступного, за следующий — следующее.
   */
  prepare?: string[];
  title: string;
  text: string;
  /** Шаг засчитывается нажатием по самой кнопке. */
  needsClick?: boolean;
  /** Шаг засчитывается делом на доске: условие проверяется каждый кадр. */
  doneWhen?: (ctx: TourCtx, start: TourStart) => boolean;
  /** Подпись действия. */
  action?: string;
  /** Что сказать, если цели на экране нет. */
  missingNote?: string;
  /**
   * Признак «уже открыто». Пока он на экране, ничего не нажимаем.
   *
   * Без него шаг нажимал кнопку снова и снова: цель могла на мгновение
   * пропасть при перерисовке, а нажатие по уже открытому окошку его
   * закрывает — окошко моргало, а карточка прыгала с места на место.
   */
  preparedWhen?: string;
};

/**
 * Что было на доске к началу шага.
 *
 * Шаг «сделайте» обязан смотреть на РАЗНИЦУ, а не на наличие. Иначе на живой
 * доске, где уже есть и текст, и фигуры, и стикеры, условие выполнено с самого
 * начала — шаги проносятся сами собой по нескольку штук, и обучение выглядит
 * сломанным. На пустой доске этого не видно, оттого и не попалось раньше.
 */
type TourStart = {
  count: number;
  snapshot: string;
  /** Сколько объектов каждого вида было. */
  types: Record<string, number>;
  /** Что было выделено. */
  selection: string;
  /** Сколько надписей УЖЕ было с текстом внутри. */
  filledText: number;
  /** Что показывали калькуляторы на начало шага. */
  calc: string;
};

function countTypes(objects: Record<string, unknown>[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const o of objects) {
    const t = String(o.type);
    out[t] = (out[t] ?? 0) + 1;
  }
  return out;
}

/** Стало ли объектов этих видов больше, чем было к началу шага. */
function grew(ctx: TourCtx, start: TourStart, ...types: string[]): boolean {
  const было = types.reduce((sum, t) => sum + (start.types[t] ?? 0), 0);
  return countOfType(ctx, ...types) > было;
}

/** Слепок положений объектов — по нему видно, что что-то передвинули. */
function snapshotPositions(objects: Record<string, unknown>[]): string {
  return objects
    .map((o) => `${String(o.id)}:${Math.round(Number(o.x) || 0)}:${Math.round(Number(o.y) || 0)}`)
    .join("|");
}

/** Непустые надписи и отпечаток калькуляторов — считает tourProgress. */
function filledTextOf(ctx: TourCtx): number {
  return filledTextCount(ctx.objects());
}

function calcStateOf(ctx: TourCtx): string {
  return calcSignature(ctx.objects());
}

function countOfType(ctx: TourCtx, ...types: string[]): number {
  return ctx.objects().filter((o) => types.includes(String(o.type))).length;
}

const STEPS: TourStep[] = [
  {
    id: "hello",
    title: "Давайте пройдёмся по доске",
    text: "Пройдём по доске шаг за шагом. Где-то нужно нажать кнопку, где-то — сделать что-то руками: нарисовать, выделить, стереть, поставить виджет. Всё происходит на вашей доске, и всё возвращается отменой (Ctrl+Z).",
  },
  {
    id: "pen",
    targets: ['[aria-label="Ручка"]', '[title^="Ручка"]'],
    title: "Перо — чем пишут",
    text: "Главный инструмент урока: решение, пометки, стрелка к ошибке.",
    needsClick: true,
    action: "Нажмите на перо",
  },
  {
    id: "draw",
    title: "А теперь напишите что-нибудь",
    text: "Проведите по доске — как мелом. Это и есть всё рисование: зажали и ведёте.",
    action: "Нарисуйте линию на доске",
    doneWhen: (ctx, start) => grew(ctx, start, "path"),
  },
  {
    id: "colors",
    targets: [".wb-color-slots__dot"],
    prepare: ['[aria-label="Ручка"]'],
    title: "Три цвета под рукой",
    text: "Эти три точки вы настраиваете под себя. Обычно держат чёрный для решения, красный для ошибок и синий для пояснений.",
    needsClick: true,
    action: "Выберите цвет",
  },
  {
    id: "colors-pick",
    // Вторая цель — сама строка точек, она на экране всегда. Окошко может
    // закрыться от чего угодно (переложили панель, ушёл фокус), и шаг
    // раньше оставался без цели: карточка перескакивала и показывала
    // приписку «окошко открывается нажатием». Со стороны это выглядело
    // так, будто один и тот же шаг показали дважды.
    // Три цели по убыванию точности: окошко, строка точек, сама «Ручка».
    // Последняя есть всегда, поэтому шаг никогда не остаётся без места и
    // не показывает карточку посреди доски с припиской.
    targets: [".wb-color-pop__grid", ".wb-color-slots__row", '[aria-label="Ручка"]'],
    prepare: ['[aria-label="Ручка"]', ".wb-color-slots__dot"],
    preparedWhen: ".wb-color-pop",
    title: "Выбор цвета — в маленьком окошке",
    text: "Нажмите по цветной точке — откроется окошко: готовые цвета, ваши сохранённые и палитра по кнопке «+». Палитра сама ничего не сохраняет: цвет попадёт в «Мои», только когда нажмёте «Добавить».",
  },
  {
    id: "width",
    targets: [".wb-color-pop__widths", ".wb-color-slots__row", '[aria-label="Ручка"]'],
    prepare: ['[aria-label="Ручка"]', ".wb-color-slots__dot"],
    preparedWhen: ".wb-color-pop",
    title: "Толщина — у каждого пера своя",
    text: "Выберите толщину прямо здесь: кружок на панели станет такого же размера. Так три точки превращаются в три готовых пера — тонкое чёрное для решения, толстое красное для ошибок.",
  },
  {
    id: "pen-dash",
    // Вид линии переехал в окошко цвета — указываем на его нижнюю строку.
    // Раньше цель была прежняя полоска под точками, её больше нет, и рамка
    // подсветки садилась на строку цветов.
    targets: [".wb-color-pop__dashes", ".wb-pen-dash-row", ".wb-color-slots__row", '[aria-label="Ручка"]'],
    prepare: ['[aria-label="Ручка"]', ".wb-color-slots__dot"],
    preparedWhen: ".wb-color-pop",
    title: "Сплошная, пунктир или точки",
    text: "Три кнопки внизу того же окошка меняют вид линии, и он тоже свой у каждого пера: пунктиром удобно проводить вспомогательные — высоту, ось, невидимое ребро. То же самое делает клавиша D.",
  },
  {
    id: "draw-2",
    title: "Напишите ещё раз — другим цветом",
    text: "Так на доске появляется привычная разметка: чёрным решение, красным то, на что смотреть.",
    action: "Нарисуйте ещё одну линию",
    doneWhen: (ctx, start) => grew(ctx, start, "path"),
  },
  {
    id: "select-tool",
    targets: ['[aria-label^="Курсор"]', '[title^="Курсор"]'],
    title: "Курсор — чтобы двигать",
    text: "Пока выбрано перо, доска рисует. Чтобы брать объекты руками, нужен курсор.",
    needsClick: true,
    action: "Нажмите на курсор",
  },
  {
    id: "select",
    title: "Выделите то, что нарисовали",
    text: "Нажмите по своей линии. Вокруг неё появится рамка, а сверху — панель со свойствами.",
    action: "Выделите объект на доске",
    doneWhen: (ctx, start) =>
      ctx.selection().length > 0 && ctx.selection().join("|") !== start.selection,
  },
  {
    id: "selection-toolbar",
    targets: [".wb-selection-toolbar"],
    title: "Панель над выделенным",
    text: "Цвет, толщина, слой, комментарий, удаление. Для текста здесь же размер и начертание. Панель всегда относится к тому, что выделено.",
    missingNote: "Панель появляется, когда на доске что-то выделено.",
  },
  {
    id: "copy-buttons",
    targets: ['[aria-label="Вставить скопированное"]', '[aria-label="Отменить"]'],
    title: "Копировать и вставить без клавиатуры",
    text: "На планшете и телефоне Ctrl+C нажать нечем: копия есть в панели над выделенным, а кнопка вставки появляется рядом с отменой, как только что-то скопировано.",
    missingNote: "Кнопка вставки появляется, когда что-нибудь скопировано.",
  },
  {
    id: "move",
    title: "Передвиньте выделенное",
    text: "Тяните прямо за объект. Тонкую линию ловить точно не нужно: доска берёт её с запасом вокруг.",
    action: "Перетащите объект",
    doneWhen: (ctx, start) => snapshotPositions(ctx.objects()) !== start.snapshot,
  },
  {
    id: "undo",
    targets: ['[aria-label="Отменить"]', '[title^="Отменить"]'],
    title: "Отмена — Ctrl+Z",
    text: "Любое действие можно вернуть. Это главная кнопка для смелых опытов: на доске ничего нельзя испортить насовсем.",
  },
  {
    id: "shapes",
    targets: ['[aria-label="Фигуры"]', '[title^="Фигуры"]'],
    title: "Фигуры, прямые и стрелки",
    text: "Прямая по линейке, стрелка к выводу, прямоугольник вокруг главного.",
    needsClick: true,
    action: "Нажмите на фигуры",
  },
  {
    id: "shape-draw",
    title: "Начертите фигуру",
    text: "Протяните по доске — получится ровная фигура. По клеткам она встаёт аккуратно, без линейки.",
    action: "Начертите фигуру на доске",
    // TOUR_SHAPE_TYPES включает geom — весь второй ряд набора фигур.
    // Без него половина начерченного не засчитывалась.
    doneWhen: (ctx, start) => grew(ctx, start, ...TOUR_SHAPE_TYPES),
  },
  {
    id: "shift-line",
    // Ровную линию даёт и перо с Shift, и «Прямая» — засчитываем любую.
    // У пера с Shift получается штрих из двух точек: по нему его и узнаём.
    // Угла 45° у прилипания нет — только горизонталь и вертикаль.
    targets: ['[aria-label="Ручка"]', '[title^="Ручка"]', '[aria-label="Фигуры"]'],
    title: "Shift — ровная линия",
    text: "Зажмите Shift и проведите пером — получится ровный отрезок, а не дрожащая линия. Если он почти горизонтальный или вертикальный, то встанет ровно по клетке: оси координат и отрезки на чертеже — без линейки. Так же работает «Прямая» в фигурах. Нужна линия с небольшим наклоном, а она «прилипает»? Нажмите G — прилипание выключится, ещё раз G — включится.",
    action: "Зажмите Shift и проведите линию пером",
    doneWhen: (ctx, start) => {
      if (grew(ctx, start, "line", "arrow")) return true;
      return ctx.objects().some((o) => {
        if (o.type !== "path" || !Array.isArray(o.points) || o.points.length > 2) return false;
        // Новый штрих — его не было на начало шага.
        return !start.snapshot.includes(`${String(o.id)}:`);
      });
    },
  },
  {
    id: "shift-shape",
    title: "Shift и Alt у фигур",
    text: "С Shift прямоугольник станет квадратом, а овал — кругом. С Alt фигура рисуется ОТ ЦЕНТРА, а не от угла: удобно, когда центр известен заранее — например, окружность вокруг точки.",
  },
  {
    id: "alt-resize",
    title: "Alt при изменении размера",
    text: "Тяните за угол с зажатым Alt — объект растёт от своего центра, оставаясь на месте. Без Alt противоположный угол стоит, а объект уползает в сторону.",
  },
  {
    id: "laser",
    title: "Alt — лазерная указка",
    text: "Просто зажмите Alt и поводите мышью: у курсора загорится яркая точка, её видят все участники, а на доске ничего не остаётся. Это замена фразе «вот тут, нет, правее» — особенно на разборе чертежа.",
    action: "Зажмите Alt и поводите мышью",
  },
  {
    id: "alt-snap",
    title: "Alt при перетаскивании — по клеткам",
    text: "Пока тащите объект, Alt означает другое: прилипание к сетке. Объекты встают ровно по клеткам, и схема перестаёт «плыть» на пиксель.",
  },
  {
    id: "keys",
    targets: ['[aria-label="Отменить"]', '[title^="Отменить"]'],
    title: "Клавиши, которые экономят время",
    text: "Ctrl+Z — отменить, Ctrl+Y — вернуть, Ctrl+C и Ctrl+V — копировать и вставить, Ctrl+D — дублировать, Delete — удалить выделенное, скобки [ и ] — слой назад и вперёд. Инструменты: V курсор, P перо, T текст, N стикер, E ластик. D меняет сплошную линию на пунктир, G включает и выключает прилипание прямых. Enter на выделенной надписи открывает её на правку, а в любом окне нажимает главную кнопку.",
  },
  {
    id: "space-pan",
    title: "Пробел — не отрываясь от рисования",
    text: "Нажали пробел — в руке курсор: выделяйте и двигайте написанное. Держать клавишу не нужно — курсор остаётся ещё несколько секунд после того, как вы её отпустили, и продлевается, пока вы работаете: на ноутбуке тачпад не слушается, пока нажата клавиша. Закончили — перо возвращается само. Два коротких нажатия подряд меняют перо на ластик и обратно. Холст двигают правая и средняя кнопки мыши, колесо приближает к курсору.",
  },
  {
    id: "text",
    targets: ['[aria-label^="Текст"]', '[title^="Текст"]'],
    title: "Текст",
    text: "Условие задачи, формула, правило. Текст на доске всегда читается, в отличие от почерка мышью.",
    needsClick: true,
    action: "Нажмите на текст",
  },
  {
    id: "text-write",
    title: "Напишите слово",
    text: "Поставьте текст на доску и наберите что-нибудь. Двойное нажатие по тексту открывает его на правку.",
    action: "Добавьте текст на доску и наберите слово",
    doneWhen: (ctx, start) => filledTextOf(ctx) > start.filledText,
  },
  {
    id: "text-format",
    targets: [".wb-selection-toolbar"],
    title: "Оформление текста",
    text: "Выделите надпись — сверху появится панель: шрифт, размер (вписать любой или выбрать из списка ▾), цвет, жирный, курсив, маркер. Выделите слово прямо внутри текста — оформление ляжет только на него. Строка с «- » или «1. » в начале становится списком, Tab вкладывает пункт.",
    action: "Выделите надпись курсором",
    doneWhen: (ctx, start) => {
      const sel = ctx.selection();
      if (!sel.length || sel.join("|") === start.selection) return false;
      const texts = new Set(
        ctx.objects().filter((o) => o.type === "text").map((o) => String(o.id))
      );
      return sel.some((id) => texts.has(id));
    },
  },
  {
    id: "text-fill-link",
    targets: ['[aria-label="Фон надписи"]', '[aria-label="Ссылка"]', ".wb-selection-toolbar"],
    title: "Фон и ссылки",
    text: "«Фон» заливает надпись цветной плашкой — так выделяют правило или ответ. 🔗 (или Ctrl+K во время набора) ставит ссылку на выделенные слова; адрес, вписанный в текст, сам становится ссылкой. Щелчок по ссылке на доске открывает её в новой вкладке.",
  },
  {
    id: "copy-style",
    title: "Скопировать оформление",
    text: "Ctrl+Alt+C на объекте запоминает его оформление, Ctrl+Alt+V кладёт его на выделенные: шрифт и цвет надписи, заливку и обводку фигуры, цвет и толщину линии. Удобно, когда несколько заголовков должны выглядеть одинаково.",
  },
  {
    id: "sticky",
    targets: ['[aria-label^="Стикер"]', '[title^="Стикер"]'],
    title: "Стикер",
    text: "То же, что текст, но на цветной карточке. Стикером выносят то, что ученик должен запомнить: его видно на доске и через неделю.",
    needsClick: true,
    action: "Нажмите на стикер",
  },
  {
    id: "sticky-put",
    title: "Поставьте стикер",
    text: "Цвет стикера выбирается теми же тремя точками, что и у пера.",
    action: "Поставьте стикер на доску",
    doneWhen: (ctx, start) => grew(ctx, start, "sticky"),
  },
  {
    id: "eraser",
    targets: ['[aria-label^="Ластик"]', '[title^="Ластик"]'],
    title: "Ластик",
    text: "Стирает объект целиком, а не кусочек линии. Размер выбирается рядом: крупный стирает всё, что попало под круг.",
    needsClick: true,
    action: "Нажмите на ластик",
  },
  {
    id: "erase",
    title: "Сотрите лишнее",
    text: "Тычок стирает всё под кругом, проведение — всё по пути. Вернуть стёртое можно той же отменой.",
    action: "Сотрите что-нибудь",
    doneWhen: (ctx, start) => ctx.count() < start.count,
  },
  {
    id: "widgets",
    targets: ['[aria-label="Виджеты"]', '[title^="Виджеты"]'],
    title: "Виджеты — урок без лишних окон",
    text: "Таймер, калькулятор, графики, код, SQL, слова, опрос. Каждый закрывает кусок урока, ради которого иначе пришлось бы открывать другую программу: всё нужное остаётся прямо на доске.",
    needsClick: true,
    action: "Откройте виджеты",
  },
  {
    id: "calc-add",
    targets: ['.wb-widgets-flyout__item[title="Калькулятор на доске"]', ".wb-widgets-flyout__item"],
    prepare: ['[aria-label="Виджеты"]'],
    title: "Поставьте калькулятор",
    text: "Калькулятором тоже надо уметь пользоваться, и этому почти нигде не учат: на физике ребёнок встаёт в тупик не перед формулой, а перед тем, куда нажать.",
    needsClick: true,
    action: "Выберите «Калькулятор»",
  },
  {
    id: "calc-try",
    targets: [".wb-calc-card", ".wb-scene-obj--calculator"],
    title: "Посчитайте на нём",
    text: "Он на доске, и вы видите каждое нажатие ученика — можно поправить руку, а не ответ. Устроен как инженерный калькулятор Windows: корни, степени, скобки, а sin, cos и tan — в меню «Тригонометрия» (hyp — гиперболические, 2ⁿᵈ — обратные).",
    action: "Нажмите пару кнопок калькулятора",
    doneWhen: (ctx, start) => calcStateOf(ctx) !== start.calc,
  },
  {
    id: "calc-order",
    targets: [".wb-calc-card", ".wb-scene-obj--calculator"],
    title: "Калькулятор: сначала число, потом функция",
    text: "Как в калькуляторе Windows: сначала набираете число, потом жмёте функцию — √, x², sin, log. Она обернёт число: наберите 9 и нажмите ²√x — на экране появится √(9). Можно и наоборот: сначала функция, тогда откроется скобка, дальше число и «)». Слева вверху DEG — углы в градусах; нажмите, и станет RAD, радианы.",
  },
  {
    id: "calc-arcsin",
    targets: [".wb-calc-card__chip--menu", ".wb-calc-card", ".wb-scene-obj--calculator"],
    title: "Посчитаем arcsin",
    text: "Обратные функции — arcsin, arccos, arctg — спрятаны за кнопкой 2ⁿᵈ. Проверим на arcsin 0,5: 1) наберите 0,5; 2) откройте «Тригонометрия»; 3) нажмите 2ⁿᵈ — sin превратится в sin⁻¹; 4) нажмите sin⁻¹; 5) нажмите «=». В режиме DEG выйдет 30 — то есть 30°. Арккосинус и арктангенс — так же, через cos⁻¹ и tan⁻¹.",
    action: "Посчитайте arcsin 0,5 на калькуляторе",
    doneWhen: (ctx, start) => {
      if (calcStateOf(ctx) === start.calc) return false;
      return ctx.objects().some((o) => {
        if (o.type !== "calculator" || !Array.isArray(o.history)) return false;
        // Журнал калькулятора кладёт свежее вычисление первым.
        const last = o.history[0];
        return typeof last === "string" && last.includes("asin(");
      });
    },
  },
  {
    id: "widget-size",
    targets: [".wb-calc-card", ".wb-scene-obj--calculator"],
    title: "Виджет крупнее — для всех",
    text: "Нажмите «Передвинуть» в шапке виджета — он выделится как объект доски, и появятся ручки по углам. Потяните за угол: цифры и кнопки растут вместе с рамкой. Калькулятор или таймер можно показать крупно, чтобы ученику было видно и на телефоне.",
  },
  {
    id: "timer",
    targets: ['.wb-widgets-flyout__item[title="Обратный отсчёт для заданий на уроке"]', '[aria-label="Виджеты"]'],
    prepare: ['[aria-label="Виджеты"]'],
    title: "Таймер на самостоятельное решение",
    text: "Виден обоим: ребёнок сам следит за временем, а вы не подгоняете его голосом. Заодно учит распределять время — на экзамене за это теряют больше, чем за незнание темы.",
  },
  {
    id: "poll",
    targets: [
      '.wb-widgets-flyout__item[title="Быстрый вопрос классу: кто как решил"]',
      '[aria-label="Виджеты"]',
    ],
    prepare: ['[aria-label="Виджеты"]'],
    title: "Опрос — быстрый вопрос",
    text: "Вопрос с вариантами прямо на доске: отвечают все сразу, и видно, кто как решил. Так проверяют, поняли ли тему, не вызывая по одному. Варианты не обязательно «да / нет»: «Изменить варианты» — и впишите свои, например «x = 2», «x = 3», «нет корней», до восьми штук.",
  },
  {
    id: "graphs",
    targets: [
      '.wb-widgets-flyout__item[title="График функций как на ОГЭ и ЕГЭ"]',
      '[aria-label="Виджеты"]',
    ],
    prepare: ['[aria-label="Виджеты"]'],
    title: "Графики функций",
    text: "Несколько функций на одних осях, область определения, отмеченные точки. Параболу больше не надо рисовать от руки, и сразу видно, как её двигает коэффициент.",
  },
  {
    id: "code-sql",
    targets: [
      '.wb-widgets-flyout__item[title="Базы данных и SQL: задания с проверкой прямо на доске"]',
      '.wb-widgets-flyout__item[title="Python, JavaScript или HTML/CSS прямо на доске"]',
      '[aria-label="Виджеты"]',
    ],
    prepare: ['[aria-label="Виджеты"]'],
    title: "Код и SQL с проверкой",
    text: "Python, JavaScript и HTML запускаются прямо на доске. В SQL-виджете задание проверяется по-настоящему: ученик пишет запрос и сразу видит таблицу с ответом.",
  },
  {
    id: "frames",
    targets: ['[title="Фреймы на доске"]', '[aria-label^="Фреймы"]'],
    title: "Фреймы — страницы занятия",
    text: "Рамка вокруг куска доски: «Разбор домашки», «Новая тема», «Задачи на дом». По ним удобно прыгать на занятии и выгружать их в PDF.",
  },
  {
    id: "pdf",
    targets: ['[aria-label="Добавить презентацию"]', '[title^="Презентация / PDF"]'],
    title: "PDF и презентации",
    text: "Файл раскладывается на слайды, показ — во весь экран, поверх слайда можно писать. Разбор остаётся рядом с условием, а не в отдельной тетради.",
  },
  {
    id: "image",
    targets: ['[aria-label="Добавить изображение"]', '[title="Изображение"]'],
    title: "Картинка на доску",
    text: "Фотография задачи из учебника, схема, чертёж. Ученик присылает фото — вы кладёте его на доску и разбираете прямо поверх.",
  },
  {
    id: "voice",
    targets: ['[aria-label^="Записать голосовое"]'],
    title: "Голосовое сообщение",
    text: "Записывается прямо на доску. Нужно на языках — произношение не напишешь буквами, — и чтобы оставить ученику объяснение после урока.",
  },
  {
    id: "snapshots",
    targets: ['[title="Сохранить и вернуть состояния доски"]'],
    title: "Снимки доски",
    text: "Сохраняет состояние доски целиком. Удобно держать чистую заготовку урока: провели занятие — вернули снимок и начали со следующим учеником с того же места.",
  },
  {
    id: "share",
    targets: ['[title^="Скопировать гостевую ссылку"]'],
    title: "Ссылка ученику",
    text: "Ученик открывает доску по ссылке — без регистрации и установки. Он сразу видит ваш курсор и то, что вы пишете.",
    missingNote: "Кнопка ссылки есть у владельца доски и редактора.",
  },
  {
    id: "call",
    targets: [".wb-board-call-toggle"],
    title: "Звонок прямо на доске",
    text: "Голос и видео не в отдельной программе, а рядом с доской: не нужно переключаться между окнами посреди объяснения.",
  },
  {
    id: "zoom",
    targets: [".wb-zoom-readout"],
    title: "Масштаб",
    text: "Колесо мыши приближает к курсору, два пальца на тачпаде двигают доску. Кнопка «Рука» слева двигает её пальцем, когда в руке перо. Клетка при этом у всех одна и та же — приближение её только увеличивает.",
  },
  {
    id: "fit",
    targets: ['[aria-label^="Показать всё"]', ".wb-zoom-btn--fit"],
    title: "Показать всё",
    text: "Возвращает к целой доске, когда уехали в сторону и потеряли, где что. Рядом кнопка мини-карты — по ней видно всю доску сразу.",
  },
  {
    id: "info",
    targets: ['[title="Название и описание доски"]', '[title^="Название и описание"]'],
    title: "Название и описание",
    text: "Подпишите доску: кому она и что на ней. Описание видно и в кабинете — через месяц вы найдёте нужную доску по нему, а не по дате.",
  },
  {
    id: "end-session",
    targets: [".wb-board-end-session-btn"],
    title: "Завершить сессию",
    text: "Конец урока одной кнопкой: доска дожидается, пока всё сохранится, можно поправить название и оставить пару слов о занятии, а на Pro — ещё и снимок урока с датой. Потом — сразу в кабинет.",
    missingNote: "Кнопка «Завершить сессию» есть у владельца доски.",
  },
  {
    id: "help",
    targets: ['[title="Справка по доске"]', '[aria-label^="Справка"]'],
    title: "Справка и это обучение",
    text: "В «Справке» — горячие клавиши и всё по мелочам. Обучение можно пройти ещё раз в любой момент: кнопка «Обучение» рядом.",
  },
];

/**
 * Разделы обучения. Названы по первому шагу раздела — отдельное поле у
 * каждого шага для этого не нужно, а список шагов остаётся плоским.
 */
const CHAPTER_MARKS = [
  { title: "Знакомство", from: "hello" },
  { title: "Перо и цвет", from: "pen" },
  { title: "Выделение", from: "select-tool" },
  { title: "Фигуры и линии", from: "shapes" },
  { title: "Клавиши", from: "keys" },
  { title: "Текст и стикеры", from: "text" },
  { title: "Ластик", from: "eraser" },
  { title: "Виджеты", from: "widgets" },
  { title: "Файлы и урок", from: "frames" },
  { title: "Ученик рядом", from: "share" },
  { title: "Напоследок", from: "zoom" },
];

/**
 * Короткий обход: то, без чего урок не провести. Полный никуда не делся —
 * но сорок с лишним шагов перед первым занятием мало кто досиживает.
 */
const SHORT_IDS = [
  "hello",
  "pen",
  "draw",
  "colors",
  "select-tool",
  "move",
  "shapes",
  "text",
  "text-write",
  "eraser",
  "widgets",
  "calc-add",
  "share",
  "end-session",
  "help",
];

const SHORT_STEPS = STEPS.filter((s) => SHORT_IDS.includes(s.id));

type TourMode = "full" | "short";

/** Где закрыли обучение: маршрут и шаг. */
const RESUME_KEY = "wb-board-tour-at";

function loadResume(): { mode: TourMode; id: string } | null {
  try {
    const raw = localStorage.getItem(RESUME_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as { mode?: string; id?: string };
    if (!data || typeof data.id !== "string") return null;
    return { mode: data.mode === "short" ? "short" : "full", id: data.id };
  } catch {
    // Приватный режим или чужой мусор в ключе — просто начнём сначала.
    return null;
  }
}

function saveResume(mode: TourMode, id: string): void {
  try {
    localStorage.setItem(RESUME_KEY, JSON.stringify({ mode, id }));
  } catch {
    /* не запомнилось — не беда */
  }
}

function clearResume(): void {
  try {
    localStorage.removeItem(RESUME_KEY);
  } catch {
    /* нечего чистить */
  }
}

type Rect = { x: number; y: number; w: number; h: number; radius: string };

function findBySelectors(selectors: string[] | undefined): HTMLElement | null {
  if (!selectors) return null;
  for (const sel of selectors) {
    let el: Element | null = null;
    try {
      el = document.querySelector(sel);
    } catch {
      el = null;
    }
    if (isUsableTarget(el)) return el;
  }
  return null;
}

/** Запас вокруг цели: между кнопкой и кольцом. */
const RING_PAD = 8;

function rectOf(el: HTMLElement): Rect {
  const r = el.getBoundingClientRect();
  // Кольцо повторяет форму кнопки: своим скруглением оно вылезало уголками
  // за её округлые углы. У круглой кнопки радиус в процентах — тогда и
  // кольцо круглое, иначе берём её радиус и добавляем запас.
  const own = getComputedStyle(el).borderTopLeftRadius || "0px";
  const radius = own.includes("%")
    ? "50%"
    : `${Math.round((parseFloat(own) || 0) + RING_PAD)}px`;
  return { x: r.x, y: r.y, w: r.width, h: r.height, radius };
}

/** Доске от обучения нужно немногое: что на ней лежит и что выделено. */
type TourScene = {
  getObjects: () => unknown[];
  getSelection: () => unknown[];
};

export function BoardTour({
  scene,
  onClose,
}: {
  scene: TourScene | null;
  onClose: () => void;
}) {
  // Пока маршрут не выбран, показываем первый экран, а не первый шаг.
  const [mode, setMode] = useState<TourMode | null>(null);
  const [index, setIndex] = useState(0);
  const [chaptersOpen, setChaptersOpen] = useState(false);
  /** Где закрыли в прошлый раз — читаем один раз при открытии. */
  const [resume] = useState(() => loadResume());
  const steps = mode === "short" ? SHORT_STEPS : STEPS;
  const chapters = useMemo(
    () => chaptersOf(steps.map((s) => s.id), CHAPTER_MARKS),
    [steps]
  );
  const [rect, setRect] = useState<Rect | null>(null);
  const targetRef = useRef<HTMLElement | null>(null);
  const startRef = useRef<TourStart>({
    count: 0,
    snapshot: "",
    types: {},
    selection: "",
    filledText: 0,
    calc: "",
  });
  /** Когда в последний раз сами открывали окошко инструмента. */
  const preparedAtRef = useRef(0);
  /** Сколько раз уже пробовали открыть: больше трёх — значит не выходит. */
  const prepareTriesRef = useRef(0);
  /**
   * Нашли ли уже место для карточки.
   *
   * Пока не нашли, карточку не показываем вовсе. Раньше она успевала
   * мелькнуть посреди доски без затемнения, а через кадр-другой прыгала к
   * кнопке вместе с пелёнкой — и выглядело это так, будто шаг показали
   * дважды.
   */
  const [settled, setSettled] = useState(false);
  /** Высота карточки: тексты разной длины, и держать её «на глаз» нельзя. */
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardH, setCardH] = useState(240);
  /**
   * Когда шаг начался: заметку «кнопки не видно» показываем не сразу.
   *
   * Время держим в ссылке, а не в состоянии: состояние обновляется своим
   * эффектом, и первый же кадр нового шага успевал посчитать возраст по
   * ПРОШЛОМУ шагу — заметка вспыхивала сразу, а карточка на миг улетала
   * вниз, будто обучение сбилось.
   */
  const stepStartedRef = useRef(Date.now());
  const [stepAge, setStepAge] = useState(0);
  /** Последнее место карточки: держим его, пока цель на миг пропала. */
  const lastPlacementRef = useRef<{ left: number; top: number } | null>(null);
  const step = steps[index];

  const ctx: TourCtx = {
    objects: () => (scene ? (scene.getObjects() as Record<string, unknown>[]) : []),
    selection: () => (scene ? (scene.getSelection() as string[]) : []),
    count: () => (scene ? scene.getObjects().length : 0),
  };
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;
  const cardHRef = useRef(240);

  const goNext = useCallback(() => {
    setIndex((i) => (i + 1 >= steps.length ? i : i + 1));
  }, [steps]);

  /**
   * Пройденным считаем только дойденное до конца.
   *
   * Раньше отметка ставилась и при закрытии — человек выходил на втором шаге,
   * а звёздочка у кнопки пропадала навсегда, будто обучение пройдено.
   */
  const finish = useCallback(
    (completed: boolean) => {
      if (completed) {
        try {
          localStorage.setItem("wb-board-tour-done", "1");
        } catch {
          /* приватный режим — просто не запомним */
        }
        // Дошли до конца — продолжать больше нечего.
        clearResume();
        // Полоска «пройдите обучение» уходит сразу, не дожидаясь
        // перезагрузки страницы.
        window.dispatchEvent(new Event("wb-tour-done"));
      }
      onClose();
    },
    [onClose]
  );

  // Запоминаем доску на начало шага: по ней видно, что человек сделал.
  useEffect(() => {
    const objects = ctxRef.current.objects();
    startRef.current = {
      count: objects.length,
      snapshot: snapshotPositions(objects),
      types: countTypes(objects),
      selection: ctxRef.current.selection().join("|"),
      filledText: filledTextOf(ctxRef.current),
      calc: calcStateOf(ctxRef.current),
    };
    preparedAtRef.current = 0;
    prepareTriesRef.current = 0;
    setStepAge(0);
    // Шагу без цели ждать нечего — карточка появляется сразу.
    setSettled(!steps[index]?.targets);
  }, [index, steps]);

  // Запоминаем место: закрыли на полуслове — в следующий раз предложим
  // продолжить отсюда, а не гонять человека по пройденному заново.
  useEffect(() => {
    const cur = steps[index];
    if (mode && cur) saveResume(mode, cur.id);
  }, [mode, index, steps]);

  // Следим за целью и за условием шага.
  useEffect(() => {
    if (!step) return;
    // Отсчёт шага начинается здесь же, до первого кадра наблюдения.
    stepStartedRef.current = Date.now();
    lastPlacementRef.current = null;
    // Цель прошлого шага забываем сразу. Иначе нажатие, которым обучение
    // само открывает панель, попадало по ЕЩЁ НЕ ОБНОВЛЁННОЙ цели и
    // засчитывалось как «нажмите кнопку»: шаг 4 тут же улетал обратно на 5,
    // и вернуться на него было нельзя.
    targetRef.current = null;
    let raf = 0;
    let advanced = false;
    const tick = () => {
      let el = findBySelectors(step.targets);
      const alreadyPrepared = step.preparedWhen
        ? Boolean(findBySelectors([step.preparedWhen]))
        : false;
      if (!el && step.prepare && !alreadyPrepared) {
        // Цель внутри окошка инструмента — откроем его сами. Не чаще раза в
        // полторы секунды: проверка идёт каждый кадр, и без выдержки это
        // превратилось бы в шестьдесят нажатий в секунду.
        const now = Date.now();
        if (now - preparedAtRef.current > 1200 && prepareTriesRef.current < 3) {
          preparedAtRef.current = now;
          prepareTriesRef.current += 1;
          // Нажимаем самое глубокое доступное звено цепочки: сперва это
          // кнопка инструмента, на следующем заходе — кнопка внутри
          // открывшегося окошка.
          const links = step.prepare.map((sel) => findBySelectors([sel]));
          const pickAt = pickOpenerIndex(
            links.map((el) =>
              el ? { expanded: el.getAttribute("aria-expanded") === "true" } : null
            )
          );
          const opener = pickAt === null ? null : links[pickAt] ?? null;
          if (opener) {
            opener.click();
            // Открываем ОДИН раз за шаг. Если окошко потом закроется, шаг
            // спокойно покажет на строку точек и не будет дёргать его туда-
            // сюда: открыл — закрылось — открыл, и так без конца.
            prepareTriesRef.current = 3;
            el = findBySelectors(step.targets);
          }
        }
      }
      targetRef.current = el;
      setRect(el ? rectOf(el) : null);
      // Цель нашлась — показываем. Не нашлась за две секунды (кнопка правда
      // спрятана) — показываем тоже, иначе карточка не появится никогда.
      if (el || Date.now() - stepStartedRef.current > 2000) {
        setSettled(true);
      }
      const h = cardRef.current?.offsetHeight;
      if (h && Math.abs(h - cardHRef.current) > 2) {
        cardHRef.current = h;
        setCardH(h);
      }
      setStepAge(Date.now() - stepStartedRef.current);

      if (!advanced && step.doneWhen && step.doneWhen(ctxRef.current, startRef.current)) {
        advanced = true;
        window.setTimeout(goNext, 500);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [step, index, goNext]);

  // Шаг с нажатием засчитывается самим нажатием по кнопке.
  useEffect(() => {
    if (!step?.needsClick) return;
    function onClick(ev: MouseEvent) {
      const el = targetRef.current;
      if (!el) return;
      const t = ev.target as Node | null;
      if (t && (el === t || el.contains(t))) {
        window.setTimeout(goNext, 260);
      }
    }
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [step, goNext]);

  useEffect(() => {
    function onKey(ev: KeyboardEvent) {
      if (ev.key === "Escape") {
        ev.stopPropagation();
        finish(false);
      }
    }
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [finish]);

  // Первый экран: с чего начать. Шагов тут ещё нет, поэтому и доску
  // затемняем целиком — выбор занимает секунду.
  if (!mode) {
    const savedList = resume?.mode === "short" ? SHORT_STEPS : STEPS;
    const savedAt = resume ? resumeIndex(savedList.map((s) => s.id), resume.id) : 0;
    const savedStep = resume && savedAt > 0 ? savedList[savedAt] : null;
    const begin = (m: TourMode, at: number) => {
      setMode(m);
      setIndex(at);
      setSettled(false);
    };
    const w = typeof window !== "undefined" ? window.innerWidth : 1280;
    const h = typeof window !== "undefined" ? window.innerHeight : 800;
    return (
      <div className="wb-tour" role="dialog" aria-label="Обучение работе с доской">
        <div
          className="wb-tour__veil-part"
          style={{ left: 0, top: 0, width: "100%", height: "100%" }}
        />
        <div
          className="wb-tour__card wb-tour__card--start"
          style={{ left: Math.max(16, w / 2 - 180), top: Math.max(16, h / 2 - 170), width: 360 }}
        >
          <h3 className="wb-tour__title">Знакомство с доской</h3>
          <p className="wb-tour__text">
            Пройдём по доске и попробуем всё руками. Закрыть можно в любой момент —
            обучение запомнит, где вы остановились.
          </p>
          <div className="wb-tour__routes">
            {savedStep ? (
              <button
                type="button"
                className="wb-tour__route wb-tour__route--resume"
                onClick={() => begin(resume!.mode, savedAt)}
              >
                <span className="wb-tour__route-title">Продолжить с шага {savedAt + 1}</span>
                <span className="wb-tour__route-sub">{savedStep.title}</span>
              </button>
            ) : null}
            <button type="button" className="wb-tour__route" onClick={() => begin("full", 0)}>
              <span className="wb-tour__route-title">Полный обход</span>
              <span className="wb-tour__route-sub">{STEPS.length} шагов, все возможности доски</span>
            </button>
            <button type="button" className="wb-tour__route" onClick={() => begin("short", 0)}>
              <span className="wb-tour__route-title">Только главное</span>
              <span className="wb-tour__route-sub">
                {SHORT_STEPS.length} шагов, чтобы провести первый урок
              </span>
            </button>
          </div>
          <div className="wb-tour__footer">
            <button type="button" className="wb-tour__skip" onClick={() => finish(false)}>
              Закрыть
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!step) return null;

  const last = index === steps.length - 1;
  const chapter = chapterAt(chapters, index);
  const vw = typeof window !== "undefined" ? window.innerWidth : 1280;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  const pad = RING_PAD;
  // Шаг-дело («нарисуйте», «напишите») доску не затемняет НИКОГДА, даже
  // когда заодно показывает на кнопку: затемнение перехватывает нажатия, и
  // на такой доске нельзя было начертить то, что шаг просит.
  const freeBoard = Boolean(step.doneWhen) || !step.targets;
  const hole =
    rect && !freeBoard
      ? { x: rect.x - pad, y: rect.y - pad, w: rect.w + pad * 2, h: rect.h + pad * 2 }
      : null;

  const waitingForDeed = Boolean(step.doneWhen);
  const cardW = 320;
  let cardLeft = Math.max(16, vw / 2 - cardW / 2);
  let cardTop = vh - cardH - 24;
  if (hole) {
    const spaceRight = vw - (hole.x + hole.w);
    if (spaceRight > cardW + 32) {
      cardLeft = hole.x + hole.w + 18;
      cardTop = Math.max(16, hole.y - 8);
    } else if (hole.x > cardW + 32) {
      cardLeft = hole.x - cardW - 18;
      cardTop = Math.max(16, hole.y - 8);
    } else if (hole.y + hole.h + cardH + 24 < vh) {
      cardLeft = Math.min(vw - cardW - 16, Math.max(16, hole.x + hole.w / 2 - cardW / 2));
      cardTop = hole.y + hole.h + 16;
    } else {
      cardLeft = Math.min(vw - cardW - 16, Math.max(16, hole.x + hole.w / 2 - cardW / 2));
      cardTop = Math.max(16, hole.y - cardH - 16);
    }
  }

  // Карточка обязана остаться в окне целиком: у нижних кнопок (масштаб,
  // мини-карта) она иначе уезжала за край вместе с кнопками «Дальше».
  // Высоту берём измеренную: тексты разной длины, и на глаз её не угадать —
  // при 305 точках вместо заложенных 250 карточка снова уходила за край.
  cardLeft = Math.min(Math.max(16, cardLeft), Math.max(16, vw - cardW - 16));
  cardTop = Math.min(Math.max(16, cardTop), Math.max(16, vh - cardH - 16));

  // Цель ещё не нашлась (окошко открывается), но шаг на неё рассчитан —
  // оставляем карточку там же, где она была. Иначе она на миг улетает вниз
  // и возвращается: со стороны это читается как сбой обучения.
  if (hole) {
    lastPlacementRef.current = { left: cardLeft, top: cardTop };
  } else if (step.targets && !waitingForDeed && lastPlacementRef.current) {
    cardLeft = lastPlacementRef.current.left;
    cardTop = lastPlacementRef.current.top;
  }

  return (
    <div className="wb-tour" role="dialog" aria-label="Обучение работе с доской">
      {/* Затемнение складываем из четырёх полос ВОКРУГ цели, а не из полотна
          с вырезом: вырез маской режет только рисунок, нажатия же полотно
          всё равно забирало себе — и подсвеченную кнопку нажать было нельзя. */}
      {hole ? (
        <>
          <div
            className="wb-tour__veil-part"
            style={{ left: 0, top: 0, width: "100%", height: Math.max(0, hole.y) }}
          />
          <div
            className="wb-tour__veil-part"
            style={{ left: 0, top: hole.y, width: Math.max(0, hole.x), height: hole.h }}
          />
          <div
            className="wb-tour__veil-part"
            style={{
              left: hole.x + hole.w,
              top: hole.y,
              width: Math.max(0, vw - (hole.x + hole.w)),
              height: hole.h,
            }}
          />
          <div
            className="wb-tour__veil-part"
            style={{
              left: 0,
              top: hole.y + hole.h,
              width: "100%",
              height: Math.max(0, vh - (hole.y + hole.h)),
            }}
          />
        </>
      ) : null}

      {hole ? (
        <div
          className="wb-tour__ring"
          style={{
            left: hole.x,
            top: hole.y,
            width: hole.w,
            height: hole.h,
            borderRadius: rect?.radius,
          }}
        />
      ) : null}

      <div
        className="wb-tour__card"
        ref={cardRef}
        style={{
          left: cardLeft,
          top: cardTop,
          width: cardW,
          // Невидимая, но измеряемая: высота карточки нужна, чтобы выбрать
          // ей сторону от кнопки. display: none этого бы не дал.
          opacity: settled ? 1 : 0,
          pointerEvents: settled ? undefined : "none",
        }}
      >
        <div className="wb-tour__progress" aria-hidden="true">
          <span style={{ width: progressPercent(index, steps.length) + "%" }} />
        </div>
        <div className="wb-tour__counter">
          {chapters.length > 1 ? (
            <button
              type="button"
              className="wb-tour__chapter"
              aria-expanded={chaptersOpen}
              onClick={() => setChaptersOpen((o) => !o)}
            >
              {chapter?.title ?? "Разделы"} ▾
            </button>
          ) : (
            <span>{chapter?.title}</span>
          )}
          <span className="wb-tour__counter-num">
            {index + 1} / {steps.length}
          </span>
        </div>
        {chaptersOpen ? (
          <ul className="wb-tour__chapters">
            {chapters.map((c) => (
              <li key={c.title}>
                <button
                  type="button"
                  className={
                    "wb-tour__chapters-item" +
                    (chapter && chapter.start === c.start ? " is-current" : "")
                  }
                  onClick={() => {
                    setIndex(c.start);
                    setChaptersOpen(false);
                  }}
                >
                  <span>{c.title}</span>
                  <span className="wb-tour__chapters-num">шаг {c.start + 1}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        <h3 className="wb-tour__title">{step.title}</h3>
        <p className="wb-tour__text">{step.text}</p>
        {step.action && (rect || waitingForDeed) ? (
          <p className="wb-tour__action">{step.action}</p>
        ) : null}
        {!rect && step.targets && !waitingForDeed && stepAge > 2200 ? (
          <p className="wb-tour__note">
            {step.missingNote ?? "На этом экране кнопки не видно — она прячется в «Меню»."}
          </p>
        ) : null}
        <div className="wb-tour__footer">
          <button type="button" className="wb-tour__skip" onClick={() => finish(false)}>
            Закрыть
          </button>
          <div className="wb-tour__nav">
            {index > 0 ? (
              <button
                type="button"
                className="wb-btn wb-btn--ghost wb-btn--sm"
                onClick={() => setIndex((i) => Math.max(0, i - 1))}
              >
                Назад
              </button>
            ) : null}
            {last ? (
              <button
                type="button"
                className="wb-btn wb-btn--primary wb-btn--sm"
                onClick={() => finish(true)}
              >
                Готово
              </button>
            ) : (
              <button type="button" className="wb-btn wb-btn--primary wb-btn--sm" onClick={goNext}>
                {step.needsClick || waitingForDeed ? "Пропустить шаг" : "Дальше"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Проходил ли человек обучение — чтобы предложить его один раз. */
export function boardTourDone(): boolean {
  try {
    return localStorage.getItem("wb-board-tour-done") === "1";
  } catch {
    return true;
  }
}
